import type { AxiosError } from "axios";

/**
 * Normalized API error for consistent handling in the app.
 * All axios errors are transformed to this shape before being rethrown.
 */
export interface ApiError {
  /** User-facing or server message */
  message: string;
  /** HTTP status (e.g. 400, 401, 404, 500) */
  status: number | null;
  /** Optional error code from response body (e.g. "VALIDATION_ERROR") */
  code?: string;
  /** Original response data when present (for logging or custom handling) */
  responseData?: unknown;
}

export interface LoadErrorCopy {
  title: string;
  description: string;
}

/**
 * Type guard: check if an unknown error is ApiError.
 */
export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as ApiError).message === "string" &&
    "status" in error
  );
}

/**
 * Extract a single message string from common API error response shapes.
 */
function getMessageFromResponse(data: unknown): string | null {
  if (data == null) return null;
  const d = data as Record<string, unknown>;
  if (typeof d.message === "string") return d.message;
  if (typeof d.error === "string") return d.error;
  if (typeof d.detail === "string") return d.detail;
  if (Array.isArray(d.errors) && d.errors.length > 0) {
    const first = d.errors[0];
    if (typeof first === "string") return first;
    if (typeof first === "object" && first !== null && "message" in first) {
      return String((first as { message: unknown }).message);
    }
  }
  return null;
}

/**
 * Recovers the server's message from a request made with `responseType: "blob"`.
 *
 * Axios honours the requested response type for failures as well as successes,
 * so a JSON error body arrives as a `Blob` and {@link transformAxiosError}
 * cannot read a message out of it - callers would show "Request failed with
 * status code 403" instead of the reason. Error bodies are small, so reading
 * this one back costs nothing; it is only the multi-megabyte *success* body
 * that the blob response type exists to keep off the main thread.
 *
 * Detected by the presence of `text()` rather than `instanceof Blob`. The
 * instance check fails across realms - iframes, workers, and the test DOM - and
 * what this function actually needs is the method, not the class.
 */
export async function readApiErrorMessage(error: unknown, fallback: string): Promise<string> {
  if (!isApiError(error)) return fallback;
  const data = error.responseData as { text?: () => Promise<string> } | undefined;
  if (typeof data?.text !== "function") return error.message;
  try {
    const parsed: unknown = JSON.parse(await data.text());
    return getMessageFromResponse(parsed) ?? error.message;
  } catch {
    return error.message;
  }
}

/**
 * Transform an AxiosError into a normalized ApiError. Use in interceptors or catch blocks.
 */
export function transformAxiosError(error: AxiosError<unknown>): ApiError {
  const status = error.response?.status ?? null;
  const data = error.response?.data;
  const bodyRecord = data && typeof data === "object" ? (data as Record<string, unknown>) : undefined;
  // Backend's ErrorResponse DTO serializes the reason code as "errorCode"
  // (see Carter.Application.Dtos.Responses.ErrorResponse), not "code" --
  // check both so callers can reliably branch on backend error codes
  const bodyCode = bodyRecord?.errorCode ?? bodyRecord?.code;
  const code = typeof bodyCode === "string" ? bodyCode : undefined;

  const message =
    getMessageFromResponse(data) ??
    (error.response?.statusText && error.response.statusText.length > 0 ? error.response.statusText : null) ??
    error.message ??
    "Request failed";

  return {
    message,
    status,
    ...(code && { code }),
    ...(data !== undefined && { responseData: data }),
  };
}

export function getLoadErrorCopy(
  error: unknown,
  options: {
    title: string;
    description: string;
    forbiddenTitle?: string;
    forbiddenDescription?: string;
  }
): LoadErrorCopy {
  const {
    title,
    description,
    forbiddenTitle = `${title} (403 Forbidden)`,
    forbiddenDescription = "Access denied (403). You do not have permission to view this page.",
  } = options;

  if (isApiError(error) && error.status === 403) {
    return {
      title: forbiddenTitle,
      description: forbiddenDescription,
    };
  }

  return { title, description };
}
